import { z } from 'zod';

import { UuidSchema } from './common.js';
import {
  PackageCollectionOutSchema,
  PackageDayOutSchema,
  PackageExclusionOutSchema,
  PackageFaqOutSchema,
  PackageFlightOutSchema,
  PackageHotelOutSchema,
  PackageInclusionOutSchema,
  PackageBatchOutSchema,
  PackagePaymentTermsSchema,
} from './packages.js';

/** A gallery image with its media id resolved to a public URL. */
export const PublicPackageImageSchema = z.object({
  id: UuidSchema,
  url: z.string(),
  altText: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PublicPackageImage = z.infer<typeof PublicPackageImageSchema>;

/** A hero highlight (icon + label) shown in the package hero strip. The icon
 *  media id is resolved server-side to a public URL. */
export const PublicHeroInclusionSchema = z.object({
  name: z.string(),
  iconUrl: z.string().nullable(),
});
export type PublicHeroInclusion = z.infer<typeof PublicHeroInclusionSchema>;

/** Public (website-facing) package detail. Media ids resolved to URLs; only
 *  published (ACTIVE) packages are ever returned. Paise stay numeric strings. */
export const PublicPackageDetailSchema = z.object({
  slug: z.string(),
  name: z.string(),
  shortDescription: z.string().nullable(),
  overviewHtml: z.string().nullable(),
  packageCategory: z.string().nullable(),
  packageTheme: z.string().nullable(),
  isInternational: z.boolean(),
  preferredMonths: z.array(z.string()),
  numberOfNights: z.number().int(),
  numberOfDays: z.number().int(),
  rating: z.number().nullable(),
  price: z.string(),
  strikePrice: z.string().nullable(),
  visaInsuranceHtml: z.string().nullable(),
  paymentTerms: PackagePaymentTermsSchema,
  paymentTermsHtml: z.string().nullable(), // legacy fallback when paymentTerms is empty
  costSummaryHtml: z.string().nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  mainImageUrl: z.string().nullable(),
  bannerImageUrl: z.string().nullable(),
  bannerVideoUrl: z.string().nullable(),
  ogImageUrl: z.string().nullable(),
  hotels: z.array(PackageHotelOutSchema),
  flights: z.array(PackageFlightOutSchema),
  days: z.array(PackageDayOutSchema),
  inclusions: z.array(PackageInclusionOutSchema),
  exclusions: z.array(PackageExclusionOutSchema),
  faqs: z.array(PackageFaqOutSchema),
  collections: z.array(PackageCollectionOutSchema),
  images: z.array(PublicPackageImageSchema),
  heroInclusions: z.array(PublicHeroInclusionSchema),
  tripTypeCode: z.string().nullable(),
  tripTypeName: z.string().nullable(),
  batches: z.array(PackageBatchOutSchema),
});
export type PublicPackageDetail = z.infer<typeof PublicPackageDetailSchema>;

/** A theme/collection tag shown as the card's coloured badge. */
export const PackageCardThemeSchema = z.object({
  name: z.string(),
  /** ThemesAndCollections.type, e.g. "theme" | "collection". */
  type: z.string(),
});
export type PackageCardTheme = z.infer<typeof PackageCardThemeSchema>;

/** An inclusion highlight flag (Hotels / Meals / Transfers / Guided Tours).
 *  iconUrl is resolved server-side from the hero-inclusion master, or null
 *  when the flag was derived from an inclusion label. */
export const PackageInclusionFlagSchema = z.object({
  name: z.string(),
  iconUrl: z.string().nullable(),
});
export type PackageInclusionFlag = z.infer<typeof PackageInclusionFlagSchema>;

/** Listing/carousel card. Media ids resolved to URLs; derived fields
 *  (themes, routeSummary, inclusionFlags, flightIncluded) are computed at
 *  read time and never stored. */
export const PublicPackageCardSchema = z.object({
  /** Package master id - used to attach CRM-edited highlights on landing pages. */
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  numberOfNights: z.number().int(),
  numberOfDays: z.number().int(),
  price: z.string(),
  strikePrice: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  /**
   * Main image + gallery URLs for landing-page card carousels. Always includes
   * `thumbnailUrl` first when present; empty when the package has no images.
   */
  imageUrls: z.array(z.string()).default([]),
  destinations: z.array(z.string()),
  themes: z.array(PackageCardThemeSchema),
  /**
   * Marketing blurb shown on the list-style card. Defaults to null so cards
   * cached or served by an API build that predates this field still parse.
   */
  shortDescription: z.string().nullable().default(null),
  /** e.g. "2N Ubud · 2N Kuta"; empty string when the package has no days. */
  routeSummary: z.string(),
  inclusionFlags: z.array(PackageInclusionFlagSchema),
  /**
   * The package's own inclusion lines, verbatim from the CRM ("Private pool
   * villa", "Sunset cruise") - what a hand-picked card lists as selling points.
   * Distinct from `inclusionFlags`, which is the fixed Transfer / Stay / Meals
   * icon set. Defaults to [] so cards cached by an API build that predates this
   * field still parse.
   */
  inclusionLabels: z.array(z.string()).default([]),
  flightIncluded: z.boolean(),
});
export type PublicPackageCard = z.infer<typeof PublicPackageCardSchema>;

const csvList = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
  )
  .pipe(z.array(z.string()).min(1));

/** Query params for GET /website/packages/search. All optional; arrive as
 *  strings from the URL, so numeric/boolean/csv fields are coerced here. */
export const PackageSearchQuerySchema = z.object({
  destination: z.string().trim().min(1).optional(),
  destinationId: z.string().trim().min(1).optional(),
  /**
   * Multi-select form of `destinationId`, for sidebars that let a visitor tick
   * several destinations. Combined with `destinationId` as a single OR set.
   */
  destinationIds: csvList.optional(),
  /** Collection marketing tag ids, e.g. "Most Loved Trips" / "Romantic escapes". */
  collections: csvList.optional(),
  /** Theme marketing tag ids, e.g. "Beach Escapes" / "Mountain Retreats". */
  themes: csvList.optional(),
  /**
   * `TripType.code` values (the package form's "Trip category" dropdown), e.g.
   * "community" / "group". Scopes a category page's listing to the trip type it
   * is about; absent means every trip type, so existing callers are unaffected.
   */
  tripTypes: csvList.optional(),
  nightsMin: z.coerce.number().int().min(0).optional(),
  nightsMax: z.coerce.number().int().min(0).optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  months: csvList.optional(),
  // NOT z.coerce.boolean(): that coerces every non-empty string (incl. "false")
  // to true, which would silently invert this filter. Only the literal "true"
  // (or a real boolean true) enables the flights-only filter; anything else is
  // false, and absent stays undefined so the service skips the filter entirely.
  flightIncluded: z
    .preprocess((v) => (v === undefined ? undefined : v === true || v === 'true'), z.boolean())
    .optional(),
  isInternational: z
    .preprocess((v) => {
      if (v === undefined) return undefined;
      if (v === true || v === 'true') return true;
      if (v === false || v === 'false') return false;
      return undefined;
    }, z.boolean())
    .optional(),
  /// MICE-only scope for the /mice/{destination} listing. Same literal-"true"
  /// handling as flightIncluded: only true enables the filter, absent skips it.
  isMice: z
    .preprocess((v) => (v === undefined ? undefined : v === true || v === 'true'), z.boolean())
    .optional(),
  sort: z.enum(['newest', 'price_low', 'price_high']).default('newest'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce
    .number()
    .int()
    .transform((n) => Math.min(24, Math.max(1, n)))
    .default(12),
});
export type PackageSearchQuery = z.infer<typeof PackageSearchQuerySchema>;

export const PackageSearchResultSchema = z.object({
  items: z.array(PublicPackageCardSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  hasMore: z.boolean(),
});
export type PackageSearchResult = z.infer<typeof PackageSearchResultSchema>;

const PackageFacetTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  count: z.number().int(),
});

export const PackageFacetsSchema = z.object({
  collections: z.array(PackageFacetTagSchema),
  themes: z.array(PackageFacetTagSchema),
  /**
   * Destinations-master entries the matching packages belong to, most packages
   * first. `id` is what `PackageSearchQuery.destinationId` expects. Defaults to
   * an empty array so a response from an API build predating this field parses.
   */
  destinations: z
    .array(z.object({ id: UuidSchema, name: z.string(), count: z.number().int() }))
    .default([]),
  nights: z.object({ min: z.number().int(), max: z.number().int() }),
  budget: z.object({ min: z.number(), max: z.number() }),
  months: z.array(z.object({ value: z.string(), label: z.string(), count: z.number().int() })),
});
export type PackageFacets = z.infer<typeof PackageFacetsSchema>;
